import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white pt-24">
        <div className="container mx-auto px-6 py-24">
          <div className="max-w-2xl mx-auto text-center">
            <ShoppingBag className="mx-auto mb-6 text-[#F4B400]" size={64} />
            <h2 className="text-3xl font-bold mb-4">Your Cart is Empty</h2>
            <p className="text-gray-400 mb-8">Start shopping to add items to your cart</p>
            <Button
              onClick={() => navigate('/shop')}
              className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white pt-24">
      <section className="py-12 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-center">
            Shopping <span className="text-[#F4B400]">Cart</span>
          </h1>
        </div>
      </section>

      <section className="py-16 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <Card key={item.cartItemKey}>
                    <CardContent className="p-6">
                      <div className="flex gap-6">
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                          <p className="text-gray-600 text-sm mb-2">
                            Size: {item.size} | Color: {item.color}
                          </p>
                          <p className="text-[#F4B400] font-bold text-lg">${item.price}</p>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <button
                            onClick={() => removeFromCart(item.cartItemKey)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.cartItemKey, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center border rounded hover:bg-gray-100 transition-colors"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="w-12 text-center font-bold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartItemKey, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center border rounded hover:bg-gray-100 transition-colors"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button
                onClick={clearCart}
                variant="outline"
                className="mt-6 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                Clear Cart
              </Button>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping</span>
                      <span className="font-semibold">Calculated at checkout</span>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-xl">
                        <span className="font-bold">Total</span>
                        <span className="font-bold text-[#F4B400]">${getCartTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/checkout')}
                    size="lg"
                    className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] transition-all duration-300"
                  >
                    Proceed to Checkout
                  </Button>
                  <Button
                    onClick={() => navigate('/shop')}
                    variant="outline"
                    className="w-full mt-3"
                  >
                    Continue Shopping
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;